import { useTable } from 'react-table'
import clsx from 'clsx'
import ContentTile from '../content/ContentTile'
import TypographyParagraph from '../typography/TypographyParagraph'
import {
	ArrowDownIcon,
	ArrowUpIcon,
	OpenInNewIcon,
	WebsiteIcon,
} from '../icons'
import { useMemo, useRef } from 'react'
import { truncateStringInMiddle } from '#app/utils/misc.tsx'
import InternalLink from '../common/internalLink'
import PlaceholderImage from '#images/image-placeholder.jpg'
import { AttestationType } from '#types/loreTypes.tsx'

const EventRow = ({ value, column, row }) => {
	return (
		<ContentTile
			variant="collection"
			title={row.original.event ? row.original.event.eventTitle : 'Unknown'}
			description={''}
			image={
				row.original?.event?.eventImageUrl
					? row.original?.event?.eventImageUrl
					: PlaceholderImage
			}
			isVerified={false}
			showBorder={false}
			showCaret={false}
		/>
	)
}

export default function EventsTable({
	eventsAttestations,
	atomicLoreUrl,
}: {
	eventsAttestations: AttestationType[]
	atomicLoreUrl: string
}) {
	const tableContainerRef = useRef(null)

	const columns = [
		{
			Header: 'Date created',
			id: 'dateCreated',
			accessor: row => (
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{new Date(row.createdAt).toLocaleDateString()}
				</TypographyParagraph>
			),
		},
		{
			Header: 'Event Title',
			id: 'eventTitle',
			Cell: EventRow,
		},
		/*{
			Header: 'Items',
			id: 'items',
			accessor: row => (
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{0}
				</TypographyParagraph>
			),
		},*/
		{
			Header: 'Event date',
			id: 'eventDate',
			accessor: row => (
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{new Date(row.event?.dateStart).toLocaleDateString()}
				</TypographyParagraph>
			),
		},
		{
			Header: 'Main Signer',
			id: 'mainSigner',
			accessor: row => (
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{truncateStringInMiddle(row.address)}
				</TypographyParagraph>
			),
		},
		{
			Header: 'Event Page',
			id: 'eventPage',
			accessor: row => (
				<InternalLink
					to={`/metadataProfile/event/${row.references.attestationIPFSCID}`}
					target="_blank"
					additionalClasses="flex flex-row items-center justify-center w-full h-full"
				>
					<WebsiteIcon size="small" />
				</InternalLink>
			),
		},
		{
			Header: 'Proof',
			id: 'proof',
			accessor: row => (
				<InternalLink
					to={`/metadataProfile/rollup/${row.references.attestationIPFSCID}`}
					target="_blank"
					additionalClasses="flex flex-row items-center justify-center w-full h-full"
				>
					<OpenInNewIcon width="20" height="20" />
				</InternalLink>
			),
		},
	]

	//  // Get state and functions for building table
	const tableData = useMemo(() => eventsAttestations, [eventsAttestations])

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		footerGroups,
		rows,
		prepareRow,
	} = useTable({
		columns,
		data: tableData,
		autoResetHiddenColumns: false,
	})

	return (
		<div className="overflow-x-auto">
			<table {...getTableProps()} className="min-w-full table-auto">
				<thead className="py-6">
					{headerGroups.map((headerGroup, index) => (
						<tr
							key={index}
							className="border-b border-textPrimaryDark pb-4 font-abc text-paragraphSmall leading-6 text-textPrimaryLight dark:border-textPrimaryLight dark:text-textPrimaryDark"
						>
							{headerGroup.headers.map((column, index) => {
								return (
									<th
										{...column.getHeaderProps()}
										key={index}
										scope="col"
										className={clsx(
											column.Header === 'Date created' &&
												'min-w-[100px] md:table-cell',
											column.Header === 'Event Title' &&
												'min-w-[100px] md:table-cell',
											column.Header === 'Items' &&
												'min-w-[100px] md:table-cell',
											column.Header === 'Event date' &&
												'min-w-[100px] md:table-cell',
											column.Header === 'Main Signer' &&
												'min-w-[100px] md:table-cell',
											column.Header === 'Event Page' &&
												'min-w-[70px] md:table-cell',
											column.Header === 'Proof' && 'min-w-[50px] md:table-cell',
											'relative h-10 py-0 pl-0 pr-2 text-left align-text-bottom font-normal',
										)}
									>
										{column.render('Header')}
										<span className="absolute top-[5px]">
											{column.isSorted ? (
												column.isSortedDesc ? (
													<ArrowDownIcon className="ml-[2px] h-3 w-3" />
												) : (
													<ArrowUpIcon className="ml-[2px] h-3 w-3" />
												)
											) : (
												''
											)}
										</span>
									</th>
								)
							})}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{rows.map((row, index) => {
						prepareRow(row)
						return (
							<tr
								key={`${row.original.references.metadataIPFSCID}`}
								className={clsx(
									'h-[72px] text-sm leading-6 text-textPrimaryLight hover:bg-grey100 dark:text-textPrimaryDark dark:hover:bg-[#161616]',
								)}
							>
								{row.cells.map((cell, index) => {
									return (
										<td
											key={`${row.original.references.metadataIPFSCID}-${cell.column.id}`}
											className={clsx(
												cell.column.Header === 'Date created' &&
													'min-w-[100px] md:table-cell',
												cell.column.Header === 'Event Title' &&
													'min-w-[100px] md:table-cell',
												cell.column.Header === 'Items' &&
													'min-w-[100px] md:table-cell',
												cell.column.Header === 'Event date' &&
													'min-w-[100px] md:table-cell',
												cell.column.Header === 'Main Signer' &&
													'min-w-[100px] md:table-cell',
												cell.column.Header === 'Event Page' &&
													'min-w-[70px] md:table-cell',
												cell.column.Header === 'Proof' &&
													'min-w-[50px] md:table-cell',
												'whitespace-no-wrap px-0 py-0 text-left',
											)}
										>
											{cell.render('Cell', {
												atomicLoreUrl,
											})}
										</td>
									)
								})}
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}
